/**
 * How a story uses its dates, as its author set it: in the Studio's Dates control, or by asking the team
 * in chat. Asset stores it on the draft and the published story as `time`. Every key but the version is
 * optional, and an absent key keeps the automatic behaviour:
 *
 *  - `field_key`: the register column that dates each step. Absent: the Date or an observation column.
 *    Where a step's register has the column, it alone dates the step, whatever its type, but its value
 *    must still be a story date.
 *  - `order`: "date" keeps the steps in date order whenever the story changes (Asset sorts them on save;
 *    steps with no date go last); "manual" keeps the author's order.
 *  - `precision`: how exactly every date shows. Absent: each row's own "Date precision".
 *  - `date_card`: false hides the date card and the timeline while the story plays.
 *
 * Import-free, so node's type-stripping test runner can load it.
 */

export const BRAIN_TEAM_STORY_TIME_ORDERS = ['date', 'manual'] as const;
export const BRAIN_TEAM_STORY_TIME_PRECISIONS = ['year', 'month', 'day'] as const;
export const BRAIN_TEAM_STORY_TIME_FIELD_KEY_LENGTH = 96;

export type BrainTeamStoryTimeOrder = (typeof BRAIN_TEAM_STORY_TIME_ORDERS)[number];
export type BrainTeamStoryTimePrecision = (typeof BRAIN_TEAM_STORY_TIME_PRECISIONS)[number];

export interface BrainTeamStoryTime {
  schema_version: 1;
  field_key?: string;
  order?: BrainTeamStoryTimeOrder;
  precision?: BrainTeamStoryTimePrecision;
  date_card?: boolean;
}

/** The parts an author changes; the version is never theirs to set. */
export type BrainTeamStoryTimeParts = Omit<BrainTeamStoryTime, 'schema_version'>;

const TIME_KEYS = new Set(['schema_version', 'field_key', 'order', 'precision', 'date_card']);

/** Strict v1 parser: only its own keys, each in range. Anything else is not a setting this app knows,
 * and reads as none, so the story keeps its automatic dates. */
export function parseBrainTeamStoryTime(value: unknown): BrainTeamStoryTime | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const present = Object.keys(raw).filter((key) => raw[key] !== undefined);
  if (present.some((key) => !TIME_KEYS.has(key)) || raw.schema_version !== 1) return undefined;
  const fieldKey = raw.field_key;
  if (
    fieldKey !== undefined &&
    (typeof fieldKey !== 'string' ||
      !fieldKey ||
      fieldKey !== fieldKey.trim() ||
      fieldKey.length > BRAIN_TEAM_STORY_TIME_FIELD_KEY_LENGTH)
  ) {
    return undefined;
  }
  const order = raw.order;
  if (order !== undefined && !BRAIN_TEAM_STORY_TIME_ORDERS.some((allowed) => allowed === order)) {
    return undefined;
  }
  const precision = raw.precision;
  if (precision !== undefined && !BRAIN_TEAM_STORY_TIME_PRECISIONS.some((allowed) => allowed === precision)) {
    return undefined;
  }
  if (raw.date_card !== undefined && typeof raw.date_card !== 'boolean') return undefined;
  return {
    schema_version: 1,
    ...(typeof fieldKey === 'string' ? { field_key: fieldKey } : {}),
    ...(order !== undefined ? { order: order as BrainTeamStoryTimeOrder } : {}),
    ...(precision !== undefined ? { precision: precision as BrainTeamStoryTimePrecision } : {}),
    ...(typeof raw.date_card === 'boolean' ? { date_card: raw.date_card } : {}),
  };
}

/**
 * The setting with some parts changed. A part given as undefined goes back to automatic; the others stay.
 * Undefined when no part is left, so the story is back on its automatic dates.
 */
export function withStoryTime(
  time: BrainTeamStoryTime | null | undefined,
  patch: BrainTeamStoryTimeParts,
): BrainTeamStoryTime | undefined {
  const next: Record<string, unknown> = { ...(time ?? {}) };
  delete next.schema_version;
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) delete next[key];
    else next[key] = value;
  }
  if (!Object.keys(next).length) return undefined;
  return parseBrainTeamStoryTime({ ...next, schema_version: 1 });
}
